// ec-core.js — Slab math and bill calculation.
//
// Pure functions, no DOM, no config mutation. This file is the correctness
// gate for the whole product: every taka the UI shows comes from here.
// Loads in both the browser (window.EC) and Node (module.exports).
//
// Verified against the known-value table in PROJECT.md §11 by test/core.test.js.

(function (root) {
  'use strict';

  // Resolve the rates config lazily so load order never matters. In Node the
  // caller usually requires ec-rates-config.js first (which also sets it on
  // globalThis); if not, we pull it in ourselves.
  function getRates() {
    if (root.EC_RATES) return root.EC_RATES;
    if (typeof require !== 'undefined') {
      try {
        return require('./ec-rates-config.js');
      } catch (e) {
        /* fall through to the throw below */
      }
    }
    throw new Error('EC_RATES not loaded — include assets/ec-rates-config.js first.');
  }

  // Coerce anything the UI hands us into a finite, non-negative number.
  // Empty inputs and stray text must produce 0, never NaN (PROJECT.md §7.4).
  function num(v, fallback) {
    var n = typeof v === 'number' ? v : parseFloat(v);
    if (!isFinite(n) || n < 0) return fallback === undefined ? 0 : fallback;
    return n;
  }

  /**
   * Progressive slab energy charge, lifeline-aware.
   *
   * LIFELINE IS ALL-OR-NOTHING (verified against BERC Order 2026/22):
   *   units <= threshold -> the WHOLE consumption bills at the lifeline rate.
   *   units >  threshold -> lifeline does not apply at all; everything flows
   *                         through the normal slabs starting at 0-75.
   *   So 50 units = 50 x 5.32 = 266.00, but 51 units = 51 x 6.18 = 315.18 — a
   *   jump upward of one unit costing ৳49, which is the gazette's behaviour.
   *
   * SLAB CAPACITY: PROJECT.md §7.1 sketches `cap = s.max - s.min + 1`, which
   * overcounts the first slab — {min:0, max:75} yields 76 units, not 75. The
   * expected value for 300 units (75x6.18 + 125x8.50 + 100x9.10 = 2436)
   * confirms the first slab holds 75 units. We therefore size each slab from
   * the previous slab's ceiling, which is correct for every tier and does not
   * care whether the config's first `min` is written as 0 or 1.
   *
   * @returns {{total:number, breakdown:Array, marginalRate:number,
   *            marginalIndex:number, isLifeline:boolean}}
   *          marginalRate = the rate of the highest slab actually reached. This
   *          is what savings are worth to the user, so Chunk 6 depends on it.
   *          marginalIndex = that slab's position in cat.slabs, or -1 when the
   *          bill is on lifeline / zero. Callers that need to know *which* tier
   *          the user is in (the slab-crossing alert, the colour ramp) must use
   *          this rather than matching on rate — two tiers may share a rate.
   */
  function calcEnergyCharge(units, cat) {
    var u = num(units);

    if (u <= 0) {
      return { total: 0, breakdown: [], marginalRate: 0, marginalIndex: -1, isLifeline: false };
    }

    if (cat.lifeline && u <= cat.lifeline.threshold) {
      var lifelineTotal = u * cat.lifeline.rate;
      return {
        total: lifelineTotal,
        breakdown: [
          {
            min: 0,
            max: cat.lifeline.threshold,
            units: u,
            rate: cat.lifeline.rate,
            subtotal: lifelineTotal,
            slabIndex: -1,
            isLifeline: true
          }
        ],
        marginalRate: cat.lifeline.rate,
        marginalIndex: -1,
        isLifeline: true
      };
    }

    var rem = u;
    var total = 0;
    var marginalRate = 0;
    var marginalIndex = -1;
    var breakdown = [];
    var prevMax = 0;

    for (var i = 0; i < cat.slabs.length; i++) {
      if (rem <= 0) break;
      var s = cat.slabs[i];
      var capacity = s.max === Infinity ? rem : Math.max(0, s.max - prevMax);
      var take = Math.min(rem, capacity);
      if (take > 0) {
        var subtotal = take * s.rate;
        breakdown.push({
          min: s.min,
          max: s.max,
          units: take,
          rate: s.rate,
          subtotal: subtotal,
          slabIndex: i,
          isLifeline: false
        });
        total += subtotal;
        marginalRate = s.rate;
        marginalIndex = i;
        rem -= take;
      }
      prevMax = s.max;
    }

    return {
      total: total,
      breakdown: breakdown,
      marginalRate: marginalRate,
      marginalIndex: marginalIndex,
      isLifeline: false
    };
  }

  /**
   * Resolve the fixed monthly meter rent for a connection phase.
   *
   * Returns { amount, known }. `known: false` means the config has no figure
   * for that phase (three-phase is `null` on purpose) — the caller bills ৳0 and
   * the flag travels out on the bill so the UI can say so. Guessing a number
   * here would put an invented charge in front of a paying customer.
   */
  function resolveMeterRent(phase, common) {
    var value = phase === 'three' ? common.meterRentThreePhase : common.meterRent;
    if (value === null || value === undefined) return { amount: 0, known: false };
    return { amount: num(value), known: true };
  }

  /**
   * Full monthly bill.
   *
   * METER RENT: a fixed monthly charge levied once per calendar month,
   *   regardless of consumption — a connection with zero units still owes it.
   *   This calculator models one month, so it is added exactly once. It is a
   *   UTILITY figure, not a BERC one (both gazettes defer it to the utility's
   *   prevailing rule), and it is ৳40 for a WZPDCL single-phase meter.
   *   Phase defaults to 'single', which is what LT-A residential is.
   *
   * VAT BASE: 5% of (energy + demand + meterRent). Meter rent was missing from
   *   the base before this pass, which under-stated VAT on every real bill.
   *
   * PREPAID REBATE: 0.5% of (energy + demand) — the pre-VAT subtotal EXCLUDING
   *   meter rent. The gazette says "net bill excluding VAT" without settling
   *   whether meter rent is inside it; the real receipts reconcile on this
   *   base. The alternative reading differs by ৳0.20/month.
   *
   * LATE-PAYMENT SURCHARGE: there is no on-time rebate. An on-time payer owes
   *   the standard bill with no adjustment; a payer past the due date owes 5%
   *   more. The surcharge is levied ONCE on the net payable amount
   *   (subtotal + VAT − prepaid rebate) and never compounds on itself — no
   *   surcharge-on-surcharge, no second month of interest modelled here.
   *
   * `isPrepaid` and `isLate` stay independent at this layer; a prepaid meter
   * cannot be paid late in practice, but that is a Step 1 UI rule, not a
   * maths rule.
   */
  function calcBill(opts) {
    opts = opts || {};
    var RATES = getRates();
    var company = opts.company || 'DESCO';
    var category = opts.category || 'LT-A';

    var companyDef = RATES.companies[company];
    if (!companyDef) throw new Error('Unknown distribution company: ' + company);
    var cat = companyDef.categories[category];
    if (!cat) throw new Error('Unknown customer category: ' + category + ' for ' + company);

    var units = num(opts.units);
    var load = num(opts.load);

    var energy = calcEnergyCharge(units, cat);

    var perKW = RATES.common.demandChargePerKW[category] || 0;
    var demand = load * perKW;

    var phase = opts.phase === 'three' ? 'three' : 'single';
    var rent = resolveMeterRent(phase, RATES.common);

    // The rebate base stops here — meter rent is deliberately outside it.
    var energyAndDemand = energy.total + demand;

    // The VAT base, and what the charge breakdown shows as "before VAT".
    var subtotal = energyAndDemand + rent.amount;
    var vat = subtotal * RATES.common.vat;

    var prepaid = opts.isPrepaid ? energyAndDemand * RATES.common.prepaidRebate : 0;

    // What the customer owes before any lateness is considered.
    var netPayable = subtotal + vat - prepaid;
    var lateSurcharge = opts.isLate
      ? netPayable * RATES.common.latePaymentSurcharge
      : 0;

    return {
      units: units,
      energy: energy,
      demand: demand,
      meterRent: rent.amount,
      meterRentKnown: rent.known,
      phase: phase,
      subtotal: subtotal,
      vat: vat,
      prepaid: prepaid,
      lateSurcharge: lateSurcharge,
      netPayable: netPayable,
      marginalRate: energy.marginalRate,
      marginalIndex: energy.marginalIndex,
      grandTotal: netPayable + lateSurcharge
    };
  }

  /**
   * The meter's severity state, derived from which slab the user is in.
   *
   * ONE definition, shared by the Step-2 pinned meter and the Step-3 bill, so
   * the two readouts can never disagree about what the device is showing.
   *
   *   'normal'  cheap tiers (and lifeline / zero) — sage LCD, slow pulse
   *   'warn'    at or above common.slabAlertFromIndex — the slab-climb flag
   *             rises and the pulse speeds up
   *   'danger'  the TOP tier — the LCD inverts
   *
   * Deliberately NOT a green/amber/red scale: real meters have no such lamp,
   * and DESIGN.md bans the RGB traffic-light. This returns a state name; how it
   * looks is entirely ec-styles.css's business.
   *
   * @param {number} marginalIndex from calcEnergyCharge / calcBill; -1 = none
   * @param {object} [cat] the tariff category, for the top-slab test
   */
  function severityFor(marginalIndex, cat) {
    var idx = typeof marginalIndex === 'number' ? marginalIndex : -1;
    if (idx < 0) return 'normal';

    var RATES = getRates();
    var slabs = (cat && cat.slabs) || [];
    if (slabs.length && idx >= slabs.length - 1) return 'danger';

    var from = RATES.common.slabAlertFromIndex;
    if (typeof from === 'number' && idx >= from) return 'warn';

    return 'normal';
  }

  /** The tariff category a company/category pair resolves to, or null. */
  function getCategory(company, category) {
    var RATES = getRates();
    var co = RATES.companies[company || 'DESCO'];
    if (!co) return null;
    return co.categories[category || 'LT-A'] || null;
  }

  /**
   * Monthly units for one appliance line.
   *
   * effectiveWatts = watts x dutyCycle, so users can enter the hours they
   * intuitively believe (a fridge is "on" 24h) while the maths stays honest.
   */
  function applianceUnits(watts, qty, hoursPerDay, days, dutyCycle) {
    var w = num(watts);
    var q = num(qty);
    var h = num(hoursPerDay);
    var d = num(days === undefined ? 30 : days, 30);
    var dc = num(dutyCycle === undefined || dutyCycle === null ? 1 : dutyCycle, 1);
    if (dc <= 0) dc = 1;
    if (h > 24) h = 24;
    return (w * dc * q * h * d) / 1000;
  }

  /** Look up an appliance definition by key. */
  function getAppliance(key) {
    var list = getRates().appliances;
    for (var i = 0; i < list.length; i++) {
      if (list[i].key === key) return list[i];
    }
    return null;
  }

  // Run 4 Phase 2: sensible bounds for a user-typed wattage. Anything outside
  // this is a typo, not a real appliance — the UI clamps to these and shows
  // an inline error rather than letting a stray digit blow up the bill.
  var WATTS_MIN = 1;
  var WATTS_MAX = 10000;

  /**
   * Clamp a candidate wattage into [WATTS_MIN, WATTS_MAX]. Returns `fallback`
   * (the config default) for anything unparseable, so a corrupt or missing
   * override can never reach the math as NaN or 0.
   */
  function clampWatts(v, fallback) {
    var n = typeof v === 'number' ? v : parseFloat(v);
    if (!isFinite(n)) return fallback;
    return Math.min(WATTS_MAX, Math.max(WATTS_MIN, n));
  }

  /**
   * Resolve a selection to an appliance definition.
   *
   * Selections normally reference a config appliance by key. The "অন্যান্য"
   * category lets the user add their own device, which carries its own watts on
   * the selection itself and has no config entry.
   *
   * Run 4 Phase 2: EVERY selection — catalog or custom — may carry its own
   * `watts`, overriding the config default. `sel.watts` is populated at
   * selection time (pre-filled from the config default) and stays editable
   * from there; a selection with no override at all (an old saved session
   * from before this existed) simply falls back to the catalog value.
   * Everything else about the appliance — duty cycle, recommend tag, group —
   * still comes from config untouched; only the wattage is ever overridable.
   */
  function resolveDef(sel) {
    if (!sel) return null;
    if (sel.custom) {
      return {
        key: sel.key,
        bn: sel.label || 'অন্যান্য যন্ত্র',
        en: sel.label || 'Custom appliance',
        group: sel.group || 'other',
        watts: clampWatts(sel.watts, WATTS_MIN),
        dutyCycle: sel.dutyCycle,
        custom: true,
        // Run 4 Phase 3a: the category→fallback-tag map in
        // ec-rates-config.js (categoryFallbackRecommend) is applied by
        // ec-appliances.js at creation time and stored directly on the
        // selection — this layer just passes it through unchanged, same as
        // every other field here. `store` (Run 5 Phase 2) travels the same way.
        recommend: sel.recommend || null,
        recommendTo: sel.recommendTo || null,
        recommendModel: sel.recommendModel,
        recommendFactor: sel.recommendFactor,
        store: sel.store || null
      };
    }
    var def = getAppliance(sel.key);
    if (!def) return null;

    var w = clampWatts(sel.watts, def.watts);
    if (w === def.watts) return def; // no real override — hand back the config object as-is

    var override = {};
    for (var k in def) {
      if (Object.prototype.hasOwnProperty.call(def, k)) override[k] = def[k];
    }
    override.watts = w;
    return override;
  }

  /**
   * Monthly units for a selected appliance, pulling watts + dutyCycle from
   * config so callers never have to remember the duty-cycle rule.
   */
  function unitsForSelection(sel, days) {
    var def = resolveDef(sel);
    if (!def) return 0;
    return applianceUnits(def.watts, sel.qty, sel.hours, days, def.dutyCycle);
  }

  /**
   * Total monthly units across a list of {key, qty, hours} selections.
   */
  function totalUnits(selections, days) {
    var sum = 0;
    for (var i = 0; i < (selections || []).length; i++) {
      sum += unitsForSelection(selections[i], days);
    }
    return sum;
  }

  /**
   * Per-appliance monthly cost at the user's marginal rate, sorted heaviest
   * first — the data behind the Step 3 mythbuster. Cost here is deliberately
   * marginal-rate-based, not an apportioned share of the progressive bill:
   * "what this appliance costs you at the margin" is the number that makes an
   * upgrade decision, and it lines up with the savings figures in Chunk 6.
   *
   * AGGREGATION (Run 6 Phase 2): an appliance can now carry more than one
   * independent LINE — e.g. three ceiling fans at three different wattages
   * ("+ different wattage" in Step 2) — each its own watts x qty x hours
   * contributor in `selections`. Every line sharing the same appliance `key`
   * collapses into ONE row here rather than one row per line:
   *
   *   - units and qty SUM across the lines.
   *   - watts and hours become the QTY-WEIGHTED AVERAGE across the lines
   *     (sum(watts_i * qty_i) / sum(qty_i), same for hours) — a defensible
   *     single number to show ("your fans average 70W") that carries no
   *     duty-cycle meaning of its own; it is display data only, never fed
   *     back into a unit calculation.
   *
   * Duty cycle is applied PER LINE (via applianceUnits(), same as always)
   * before the per-line units are summed into the row. Because duty cycle is
   * a constant multiplier for a given key (it comes from config, not from any
   * one line) and multiplication distributes over addition, this is
   * mathematically IDENTICAL to summing each line's raw watts x qty x hours
   * first and applying duty cycle once to the total — so a single-line
   * appliance (the common case) reduces to exactly its old watts/hours/units
   * with no change from before.
   */
  function applianceBreakdown(selections, marginalRate, days) {
    var order = [];
    var byKey = {};
    var i;

    for (i = 0; i < (selections || []).length; i++) {
      var sel = selections[i];
      var def = resolveDef(sel);
      if (!def) continue;
      var u = applianceUnits(def.watts, sel.qty, sel.hours, days, def.dutyCycle);
      if (u <= 0) continue;

      var qty = num(sel.qty);
      var hours = num(sel.hours);

      var agg = byKey[def.key];
      if (!agg) {
        agg = byKey[def.key] = {
          key: def.key,
          bn: def.bn,
          en: def.en,
          group: def.group,
          dutyCycle: def.dutyCycle || 1,
          recommend: def.recommend || null,
          custom: !!def.custom,
          units: 0,
          qty: 0,
          wattsQty: 0,
          hoursQty: 0
        };
        order.push(agg);
      }

      agg.units += u;
      agg.qty += qty;
      agg.wattsQty += def.watts * qty;
      agg.hoursQty += hours * qty;
    }

    var grandUnits = 0;
    for (i = 0; i < order.length; i++) grandUnits += order[i].units;

    var rows = order.map(function (agg) {
      return {
        key: agg.key,
        bn: agg.bn,
        en: agg.en,
        group: agg.group,
        watts: agg.qty > 0 ? agg.wattsQty / agg.qty : 0,
        dutyCycle: agg.dutyCycle,
        qty: agg.qty,
        hours: agg.qty > 0 ? agg.hoursQty / agg.qty : 0,
        units: agg.units,
        cost: agg.units * num(marginalRate),
        recommend: agg.recommend,
        custom: agg.custom,
        share: grandUnits > 0 ? agg.units / grandUnits : 0
      };
    });

    rows.sort(function (a, b) {
      return b.units - a.units;
    });

    return { rows: rows, totalUnits: grandUnits };
  }

  var EC = {
    calcEnergyCharge: calcEnergyCharge,
    calcBill: calcBill,
    resolveMeterRent: resolveMeterRent,
    severityFor: severityFor,
    getCategory: getCategory,
    applianceUnits: applianceUnits,
    getAppliance: getAppliance,
    resolveDef: resolveDef,
    unitsForSelection: unitsForSelection,
    totalUnits: totalUnits,
    applianceBreakdown: applianceBreakdown,
    clampWatts: clampWatts,
    WATTS_MIN: WATTS_MIN,
    WATTS_MAX: WATTS_MAX,
    _num: num
  };

  root.EC = EC;
  if (typeof module !== 'undefined' && module.exports) module.exports = EC;
})(typeof globalThis !== 'undefined' ? globalThis : this);
